// Site-wide single source of truth — hours, brand, contact, booking.
// Sections and schema must import from here (no hard-coded duplicates).

export const BOOKING_URL = '#rdv';

export const BRAND = {
  name: 'LIFECUPP by bien-être',
  shortName: 'LIFECUPP',
  tagline: 'Cupping thérapie & bien-être à Annemasse',
  city: 'Annemasse',
  postalCode: '74100',
  region: 'Haute-Savoie',
  country: 'FR',
  street: '5 Rue du Clos Fleury',
  phoneDisplay: '07 45 76 73 98',
  phoneE164: '+33745767398',
  phoneWhatsApp: '33745767398',
  email: 'contact@lifecupp.com',
  /** Public brand line — practitioners include Ayoub and a female practitioner. */
  teamLine: 'Praticiens formés et à l’écoute',
  logoUrl:
    'https://storage.readdy-site.link/project_files/9a8570c6-f50d-49ad-8ac8-35ff8d1a4c26/c87c44a2-d0a7-4151-ada3-d3751583b805_logo-hijama.png?v=cfa734519dfbfbbf0361451e10f38b20',
} as const;

/** Opening hours — Map + Appointment + Footer + schema all use this. */
export const HOURS = {
  weekdays: { label: 'Lundi – Vendredi', open: '09:00', close: '19:00', display: '9h00 – 19h00' },
  saturday: { label: 'Samedi', open: '09:00', close: '17:00', display: '9h00 – 17h00' },
  sunday: { label: 'Dimanche', closed: true as const, display: 'Fermé' },
  /** Compact line for sidebars / footer */
  summary: 'Lun–Ven 9h–19h · Sam 9h–17h · Dim fermé',
  rows: [
    { day: 'Lundi – Vendredi', hours: '9h00 – 19h00', closed: false },
    { day: 'Samedi', hours: '9h00 – 17h00', closed: false },
    { day: 'Dimanche', hours: 'Fermé', closed: true },
  ],
} as const;

/** Slot list for the booking form (lunch break 12:00–14:00 omitted). */
export const BOOKING_SLOTS_WEEKDAY = [
  '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
  '14:00', '14:30', '15:00', '15:30', '16:00', '16:30',
  '17:00', '17:30', '18:00', '18:30',
] as const;

/** Saturday ends at 17:00 — no slots after 16:30. */
export const BOOKING_SLOTS_SATURDAY = [
  '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
  '14:00', '14:30', '15:00', '15:30', '16:00', '16:30',
] as const;

/** Closed range from API (YYYY-MM-DD inclusive). */
export type AbsenceRange = { from: string; to: string; id?: number; label?: string | null };

const MONTHS_FR = [
  'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre',
];

function formatDayFr(iso: string): { day: number; month: number; year: number } {
  const [y, m, d] = iso.split('-').map(Number);
  return { day: d, month: m, year: y };
}

/** "du 25 au 31 août" or "du 25 août au 2 septembre". */
export function formatAbsenceRangeFr(range: AbsenceRange): string {
  const a = formatDayFr(range.from);
  const b = formatDayFr(range.to);
  const monthA = MONTHS_FR[a.month - 1];
  const monthB = MONTHS_FR[b.month - 1];
  if (a.year === b.year && a.month === b.month) {
    if (a.day === b.day) return `le ${a.day} ${monthA}`;
    return `du ${a.day} au ${b.day} ${monthA}`;
  }
  if (a.year === b.year) {
    return `du ${a.day} ${monthA} au ${b.day} ${monthB}`;
  }
  return `du ${a.day} ${monthA} ${a.year} au ${b.day} ${monthB} ${b.year}`;
}

/**
 * Hint under the date field, e.g.
 * "Indisponible du 25 au 31 août" or "Indisponible du 25 au 31 août, du 10 au 12 septembre."
 */
export function formatAbsencesHint(ranges: AbsenceRange[]): string | null {
  if (!ranges?.length) return null;
  const parts = ranges.map(formatAbsenceRangeFr);
  return `Indisponible ${parts.join(', ')}.`;
}

export function isUnavailableDate(date: string, ranges: AbsenceRange[] = []): boolean {
  return ranges.some((r) => date >= r.from && date <= r.to);
}

/** 0 = Sunday … 6 = Saturday (local date string YYYY-MM-DD). */
export function dayOfWeekFromDate(date: string): number {
  const d = new Date(`${date}T12:00:00`);
  return d.getDay();
}

export function isSunday(date: string): boolean {
  return dayOfWeekFromDate(date) === 0;
}

export function isSaturday(date: string): boolean {
  return dayOfWeekFromDate(date) === 6;
}

export function slotsForDate(date: string, absences: AbsenceRange[] = []): readonly string[] {
  if (!date || isSunday(date) || isUnavailableDate(date, absences)) return [];
  if (isSaturday(date)) return BOOKING_SLOTS_SATURDAY;
  return BOOKING_SLOTS_WEEKDAY;
}

export const ADDRESS_LINES = {
  street: BRAND.street,
  cityLine: `${BRAND.postalCode} ${BRAND.city}`,
  full: `${BRAND.street}, ${BRAND.postalCode} ${BRAND.city}`,
  region: BRAND.region,
} as const;

export const MAPS = {
  directions: `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(ADDRESS_LINES.full)}`,
  embed:
    'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2762.5!2d6.2368!3d46.1936!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x478c7b0e0e0e0e0f%3A0x0!2s5+Rue+du+Clos+Fleury%2C+74100+Annemasse!5e0!3m2!1sfr!2sfr!4v1700000000000!5m2!1sfr!2sfr',
} as const;
